import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation } from '@tanstack/react-query';
import { SparklesIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { useEarlyAdopter } from '@/contexts/EarlyAdopterContext';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useAnalytics } from '@/contexts/AnalyticsContext';

const earlyAdopterSchema = z.object({
  firstName: z.string().min(1, 'First name is required'),
  email: z.string().email('Please enter a valid email address'),
  role: z.string().min(1, 'Please select how you\'re signing up'),
});

type EarlyAdopterForm = z.infer<typeof earlyAdopterSchema>;

const EarlyAdopterModal = () => {
  const { isModalOpen, closeModal, sourcePage } = useEarlyAdopter();
  const [isSubmitted, setIsSubmitted] = useState(false);
  const { trackFormSubmission } = useAnalytics();

  // Check for preselected role from referral welcome page
  const preselectedRole = sessionStorage.getItem('preselected_role') || '';

  const form = useForm<EarlyAdopterForm>({
    resolver: zodResolver(earlyAdopterSchema),
    defaultValues: {
      firstName: '',
      email: '',
      role: preselectedRole,
    },
  });

  // Update role field when preselected role is available
  useEffect(() => {
    if (preselectedRole && isModalOpen) {
      form.setValue('role', preselectedRole);
    }
  }, [preselectedRole, isModalOpen, form]);

  const mutation = useMutation({
    mutationFn: async (data: EarlyAdopterForm) => {
      const params = new URLSearchParams(window.location.search);
      const referralCode = params.get('ref') || localStorage.getItem('referral_code') || sessionStorage.getItem('referral_code') || '';
      const selectedPlan = localStorage.getItem('referral_selected_plan') || sessionStorage.getItem('preselected_plan') || '';
      
      const meta = {
        pagePath: window.location.pathname,
        referrer: document.referrer || '',
        utmSource: params.get('utm_source') || sessionStorage.getItem('utm_source') || '',
        utmMedium: params.get('utm_medium') || sessionStorage.getItem('utm_medium') || '',
        utmCampaign: params.get('utm_campaign') || sessionStorage.getItem('utm_campaign') || '',
        utmContent: params.get('utm_content') || sessionStorage.getItem('utm_content') || '',
        utmTerm: params.get('utm_term') || sessionStorage.getItem('utm_term') || '',
        source: sourcePage || 'website',
        referralCode: referralCode || undefined,
        selectedPlan: selectedPlan || undefined
      };

      const { data: result, error } = await supabase.functions.invoke('early-adopter-signup', {
        body: {
          ...data,
          lastName: '', // Send empty for backwards compatibility
          primaryInterest: '', // Send empty for backwards compatibility
          urgencyLevel: 'exploring', // Default value for backwards compatibility
          ...meta,
        }
      });

      if (error) {
        throw new Error(error.message || 'Submission failed');
      }

      if (!result?.success) {
        throw new Error(result?.message || 'Submission failed');
      }

      return { success: true };
    },
    onSuccess: () => {
      setIsSubmitted(true);
      toast.success('You\'re on the list!');
      
      // Track Reddit Lead event
      if (typeof window !== 'undefined' && window.rdt) {
        const formData = form.getValues();
        window.rdt('track', 'Lead', {
          lead_type: 'vip_early_access',
          role: formData.role,
          source: 'early_adopter_modal',
          page_path: window.location.pathname,
        });
      }
    },
    onError: (error: any) => {
      const errorMessage = error.message || 'Something went wrong. Please try again.';
      toast.error(errorMessage);
    },
  });

  const onSubmit = (data: EarlyAdopterForm) => {
    mutation.mutate(data);
  };

  const handleClose = () => {
    closeModal();
    
    setTimeout(() => {
      setIsSubmitted(false);
      form.reset();
      mutation.reset();
    }, 300);
  };

  // Handle escape key press
  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isModalOpen) {
        handleClose();
      }
    };

    if (isModalOpen) {
      document.addEventListener('keydown', handleEscapeKey);
      return () => document.removeEventListener('keydown', handleEscapeKey);
    }
  }, [isModalOpen]);

  // Success/Confirmation screen
  if (isSubmitted) {
    return (
      <Dialog open={isModalOpen} onOpenChange={(open) => {
        if (!open) handleClose();
      }}>
        <DialogContent className="max-w-md mx-auto">
          <DialogHeader className="sr-only">
            <DialogTitle>Success</DialogTitle>
            <DialogDescription>You have successfully joined our early access list</DialogDescription>
          </DialogHeader>
          <div className="text-center py-8">
            <div className="mx-auto w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-6">
              <CheckCircleIcon className="w-8 h-8 text-primary" />
            </div>
            <h2 className="text-2xl font-bold text-foreground mb-4">You're on the list</h2>
            <p className="text-muted-foreground mb-6 leading-relaxed">
              Thanks for joining the SeniorThrive VIP early access list. We'll reach out as soon as your invitation is ready.
            </p>
            <p className="text-sm text-muted-foreground mb-8">
              In the meantime, you're welcome to explore how SeniorThrive supports life at home.
            </p>
            <Button onClick={handleClose} className="w-full">
              Continue Exploring
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // Main form
  return (
    <Dialog open={isModalOpen} onOpenChange={(open) => {
      if (!open) handleClose();
    }}>
      <DialogContent className="max-w-md mx-auto">
        <DialogHeader className="text-center pb-2">
          <div className="mx-auto w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mb-4">
            <SparklesIcon className="w-6 h-6 text-primary" />
          </div>
          <DialogTitle className="text-2xl font-bold text-foreground">
            Get VIP Early Access to SeniorThrive
          </DialogTitle>
          <DialogDescription className="text-muted-foreground mt-3 leading-relaxed">
            SeniorThrive is currently in early access. Join the VIP list to be among the first invited to start your free trial.
          </DialogDescription>
        </DialogHeader>

        <p className="text-sm text-muted-foreground text-center mb-6">
          Join individuals and families preparing to thrive at home with confidence.
        </p>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
            <FormField
              control={form.control}
              name="firstName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>First Name *</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter your first name" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email Address *</FormLabel>
                  <FormControl>
                    <Input type="email" placeholder="Enter your email" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="role"
              render={({ field }) => (
                <FormItem className="space-y-3">
                  <FormLabel>I'm signing up as: *</FormLabel>
                  <FormControl>
                    <RadioGroup
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                      className="space-y-2"
                    >
                      <div className="flex items-center space-x-3 p-3 rounded-lg border border-border hover:border-primary/30 transition-colors cursor-pointer">
                        <RadioGroupItem value="older_adult" id="older_adult" />
                        <label htmlFor="older_adult" className="text-sm font-medium cursor-pointer flex-1">
                          An older adult
                        </label>
                      </div>
                      <div className="flex items-center space-x-3 p-3 rounded-lg border border-border hover:border-primary/30 transition-colors cursor-pointer">
                        <RadioGroupItem value="family_member" id="family_member" />
                        <label htmlFor="family_member" className="text-sm font-medium cursor-pointer flex-1">
                          A family member
                        </label>
                      </div>
                      <div className="flex items-center space-x-3 p-3 rounded-lg border border-border hover:border-primary/30 transition-colors cursor-pointer">
                        <RadioGroupItem value="caregiver" id="caregiver" />
                        <label htmlFor="caregiver" className="text-sm font-medium cursor-pointer flex-1">
                          A caregiver or professional
                        </label>
                      </div>
                    </RadioGroup>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="pt-2">
              <Button type="submit" className="w-full" size="lg" disabled={mutation.isPending}>
                {mutation.isPending ? 'Joining...' : 'Join the VIP Early Access List'}
              </Button>
            </div>
          </form>
        </Form>

        <p className="text-center text-xs text-muted-foreground mt-4">
          No credit card. No obligation. You'll be invited when access opens.
        </p>
      </DialogContent>
    </Dialog>
  );
};

export default EarlyAdopterModal;
